import React from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { AaiContext, OwnerOverview, WhatsAllowedData } from '../../types/aai';
import { ErrorNote, Loading, PageBody, PageHead } from '../../components/aa';
import type { ActivityLinkResolver } from '../partner/ActivityView';
import { ClientContextBody, useLatestCollection } from '../partner/ClientContextPage';

/*
 * Company Context, for the AI Champion. The same body the Owner and the
 * Solution Partner read, rendered for a viewer whose access is portfolio
 * summaries: reviewed company patterns, approved Project summaries, reuse
 * signals and source status. The champion authors the Orbit cycle; the Owner
 * decides what it releases.
 */

const championLinks: ActivityLinkResolver = (event) => {
  if (event.entity_type === 'request' && event.entity_id) return { label: 'Open project', to: `/projects?open=${event.entity_id}` };
  if (event.entity_type === 'opportunity' && event.entity_id) return { label: 'Open portfolio', to: '/portfolio' };
  if (event.entity_type === 'skill') return { label: 'Open Quick Wins', to: '/projects?tab=quick_wins' };
  if (event.entity_type === 'member') return { label: 'Open people', to: '/org-chart' };
  if (event.entity_type === 'source') return { label: 'Open company tools', to: '/company-context?tab=sources' };
  if (event.entity_type === 'portfolio_recommendation' && event.entity_id) return { label: 'Open recommendation', to: `/portfolio?recommendation=${event.entity_id}` };
  return null;
};

const ChampionCompanyContextPage: React.FC = () => {
  const clientId = localStorage.getItem('client_id') || '';
  const clientName = localStorage.getItem('client_name') || 'Your company';

  const context = useAsync<AaiContext>(() => aaiApi.getContext(), 'Could not load your context.');
  const allowed = useAsync<WhatsAllowedData>(() => aaiApi.getWhatsAllowed(), 'Could not load company context.');
  const cycle = useLatestCollection(clientId || undefined);

  if (allowed.loading) return <PageBody><Loading label="Loading company context" /></PageBody>;
  if (allowed.error || !allowed.data) return <PageBody><ErrorNote message={allowed.error || 'Something went wrong.'} /></PageBody>;

  const companyName = context.data?.company_name || clientName;

  return (
    <PageBody>
      <PageHead
        align="center"
        title="Company Context"
        sub="What the company is trying to achieve, how work happens, who owns decisions, and what is ready to improve."
      />
      <ClientContextBody
        viewer="champion"
        clientId={clientId}
        clientName={companyName}
        allowed={allowed.data}
        cycle={cycle}
        links={championLinks}
        room={false}
      />
    </PageBody>
  );
};

export default ChampionCompanyContextPage;
